'use client';

import { CheckCircle, DollarSign, Calendar, Camera, MessageSquare, Wrench, BarChart3, Headphones, ArrowRight, Sparkles, Zap, Shield, Users, TrendingUp } from 'lucide-react';
import Image from 'next/image';

export default function PropertyOwnerBenefits() {
    const benefits = [
        {
            icon: DollarSign,
            title: "Maximize Your Revenue",
            description: "Our dynamic pricing algorithm ensures you get the best rates based on demand, seasonality, and local events.",
            features: ["Smart pricing optimization", "Seasonal rate adjustments", "Event-based pricing", "Competitive analysis"],
            image: "https://images.pexels.com/photos/5716000/pexels-photo-5716000.jpeg",
            gradient: "from-primary to-primary/80",
            bgColor: "bg-primary/5",
            iconBg: "bg-primary/10",
            iconColor: "text-primary"
        },
        {
            icon: Calendar,
            title: "Effortless Booking Management",
            description: "Automated calendar sync, instant booking confirmations, and seamless guest communication.",
            features: ["Automated calendar sync", "Instant booking confirmations", "Guest communication tools", "Check-in/out automation"],
            image: "https://images.pexels.com/photos/7688336/pexels-photo-7688336.jpeg",
            gradient: "from-secondary to-secondary/80",
            bgColor: "bg-secondary/5",
            iconBg: "bg-secondary/10",
            iconColor: "text-secondary"
        },
        // {
        //     icon: Camera,
        //     title: "Professional Marketing",
        //     description: "High-quality photography, compelling listings, and multi-channel marketing to attract premium guests.",
        //     features: ["Professional photography", "SEO-optimized listings", "Social media promotion", "Email marketing campaigns"],
        //     image: "https://images.pexels.com/photos/4348401/pexels-photo-4348401.jpeg",
        //     gradient: "from-primary via-primary/90 to-secondary",
        //     bgColor: "bg-primary/5",
        //     iconBg: "bg-primary/10",
        //     iconColor: "text-primary"
        // },
        // {
        //     icon: MessageSquare,
        //     title: "Guest Communication",
        //     description: "Automated messaging, multilingual support, and 24/7 guest assistance to ensure perfect stays.",
        //     features: ["Automated messaging system", "Multilingual support", "Pre-arrival instructions", "Post-stay follow-up"],
        //     image: "https://images.pexels.com/photos/7688460/pexels-photo-7688460.jpeg",
        //     gradient: "from-secondary via-secondary/90 to-primary",
        //     bgColor: "bg-secondary/5",
        //     iconBg: "bg-secondary/10",
        //     iconColor: "text-secondary"
        // },
        // {
        //     icon: Wrench,
        //     title: "Maintenance & Cleaning",
        //     description: "Coordinated cleaning services, maintenance scheduling, and quality inspections to maintain your property's standards.",
        //     features: ["Professional cleaning services", "Maintenance coordination", "Quality inspections", "Emergency repairs"],
        //     image: "https://images.pexels.com/photos/4239037/pexels-photo-4239037.jpeg",
        //     gradient: "from-primary to-secondary",
        //     bgColor: "bg-primary/5",
        //     iconBg: "bg-primary/10",
        //     iconColor: "text-primary"
        // },
        {
            icon: BarChart3,
            title: "Analytics & Reporting",
            description: "Detailed performance reports, revenue tracking, and insights to help you optimize your property's performance.",
            features: ["Revenue analytics", "Occupancy reports", "Guest feedback analysis", "Performance insights"],
            image: "https://images.pexels.com/photos/590020/pexels-photo-590020.jpeg",
            gradient: "from-secondary to-primary",
            bgColor: "bg-secondary/5",
            iconBg: "bg-secondary/10",
            iconColor: "text-secondary"
        }
    ];

    return (
        <section className="py-20 bg-gradient-to-br from-primary/10 to-primary/5 relative overflow-hidden">
            {/* Background decorative elements */}
            <div className="absolute top-0 left-0 w-full h-full">
                <div className="absolute top-20 left-10 w-32 h-32 bg-primary/20 rounded-full opacity-20 blur-xl"></div>
                <div className="absolute top-40 right-20 w-24 h-24 bg-secondary/20 rounded-full opacity-20 blur-xl"></div>
                <div className="absolute bottom-20 left-1/4 w-40 h-40 bg-primary/20 rounded-full opacity-20 blur-xl"></div>
            </div>

            <div className="container mx-auto px-4 relative z-10">
                {/* Header Section */}
                <div className="text-center mb-20">
                    <div className="inline-flex items-center gap-2 bg-secondary text-white px-4 py-1 rounded-full text-sm font-medium mb-6">
                        <Sparkles className="w-4 h-4" />
                        <span>Complete Property Management Solution</span>
                    </div>
                    <h2 className="text-3xl lg:text-4xl font-semibold text-gray-900 mb-6">
                        Everything You Need to <span className="text-primary">Succeed</span>
                    </h2>
                    <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                        We provide a complete solution for property owners, handling every aspect of your holiday rental business 
                        so you can focus on what you do best.
                    </p>
                </div>

                {/* Benefits Grid with Creative Layout */}
                <div className="space-y-16">
                    {benefits.map((benefit, index) => {
                        const IconComponent = benefit.icon;
                        const isEven = index % 2 === 0;
                        
                        return (
                            <div key={index} className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12`}>
                                {/* Content Side */}
                                <div className="flex-1 space-y-6">
                                    <div className="flex items-center gap-4">
                                        {/* <div className={`w-16 h-16 ${benefit.iconBg} rounded-2xl flex items-center justify-center shadow-lg`}>
                                            <IconComponent className={`w-8 h-8 ${benefit.iconColor}`} />
                                        </div> */}
                                        <div>
                                            <h3 className="text-3xl font-medium text-gray-900">
                                                {benefit.title}
                                            </h3>
                                            <div className="flex items-center gap-2 mt-2">
                                                <div className={`w-2 h-2 bg-gradient-to-r ${benefit.gradient} rounded-full`}></div>
                                                <div className={`w-8 h-0.5 bg-gradient-to-r ${benefit.gradient} rounded-full`}></div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <p className="text-base text-gray-600 leading-relaxed">
                                        {benefit.description}
                                    </p>
                                    
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {benefit.features.map((feature, featureIndex) => (
                                            <div key={featureIndex} className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
                                                <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                                                <span className="text-gray-700 font-medium">{feature}</span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* <div className="flex items-center gap-2 text-primary font-semibold group cursor-pointer">
                                        <span>Learn more</span>
                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </div> */}
                                </div>

                                {/* Image Side */}
                                <div className="flex-1 relative">
                                    <div className="relative group">
                                        <div className={`absolute -inset-4 bg-gradient-to-r ${benefit.gradient} rounded-3xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity`}></div>
                                        <div className="relative bg-white rounded-2xl p-4 shadow-2xl">
                                            <Image
                                                src={benefit.image}
                                                alt={benefit.title}
                                                width={500}
                                                height={350}
                                                className="w-full h-80 object-cover rounded-xl"
                                            />
                                            {/* <div className="absolute top-6 right-6 bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg">
                                                <IconComponent className={`w-6 h-6 ${benefit.iconColor}`} />
                                            </div> */}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Enhanced Support Section */}
                <div className="mt-24 relative">
                    <div className="bg-secondary rounded-3xl p-12 text-center text-white relative overflow-hidden">
                        {/* Background decorative elements */}
                        {/* <div className="absolute top-0 left-0 w-full h-full">
                            <div className="absolute top-10 left-10 w-20 h-20 bg-white/10 rounded-full blur-xl"></div>
                            <div className="absolute bottom-10 right-10 w-32 h-32 bg-white/5 rounded-full blur-2xl"></div>
                            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-white/5 rounded-full blur-3xl"></div>
                        </div> */}
                        
                        <div className="relative z-10 max-w-4xl mx-auto">
                            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-6">
                                <Zap className="w-4 h-4" />
                                <span>Always Here for You</span>
                            </div>
                            
                            <h3 className="text-3xl font-medium mb-6">
                                24/7 Expert Support Team
                            </h3>
                            
                            <p className="text-base opacity-90 mb-12 leading-relaxed">
                                Our dedicated support team is available around the clock to help you and your guests 
                                with any questions or issues that may arise. We're not just a platform, we're your partners in success.
                            </p>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                                <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/30 transition-all group">
                                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                                        <Headphones className="w-6 h-6" />
                                    </div>
                                    <h4 className="text-lg font-medium mb-2">Phone Support</h4>
                                    <p className="text-sm opacity-80 mb-3">Available 24/7</p>
                                    <div className="text-xs opacity-60">Average response: 30 seconds</div>
                                </div>
                                
                                <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/30 transition-all group">
                                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                                        <MessageSquare className="w-6 h-6" />
                                    </div>
                                    <h4 className="text-lg font-medium mb-2">Live Chat</h4>
                                    <p className="text-sm opacity-80 mb-3">Instant response</p>
                                    <div className="text-xs opacity-60">Available in 12 languages</div>
                                </div>
                                
                                <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/30 transition-all group">
                                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                                        <Shield className="w-6 h-6" />
                                    </div>
                                    <h4 className="text-lg font-medium mb-2">Email Support</h4>
                                    <p className="text-sm opacity-80 mb-3">Within 2 hours</p>
                                    <div className="text-xs opacity-60">Detailed solutions provided</div>
                                </div>
                            </div>
                            
                            {/* <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <div className="bg-white/20 backdrop-blur-sm rounded-full px-6 py-3 flex items-center gap-3">
                                    <Users className="w-5 h-5" />
                                    <span className="font-semibold">500+ Support Agents</span>
                                </div>
                                <div className="bg-white/20 backdrop-blur-sm rounded-full px-6 py-3 flex items-center gap-3">
                                    <TrendingUp className="w-5 h-5" />
                                    <span className="font-semibold">99.9% Uptime</span>
                                </div>
                                <div className="bg-white/20 backdrop-blur-sm rounded-full px-6 py-3 flex items-center gap-3">
                                    <Sparkles className="w-5 h-5" />
                                    <span className="font-semibold">4.9/5 Rating</span>
                                </div>
                            </div> */}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
